import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { NAV_ITEMS } from "../lib/data";

const LOGO_DARK = "/wertbild-logo.png";
const LOGO_LIGHT = "/wertbild-logo-light.png";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === "/";
  const overDark = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => {
      const heroEl = document.getElementById("start");
      const threshold = heroEl ? heroEl.offsetHeight - 100 : window.innerHeight - 100;
      setScrolled(window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [location.pathname]);

  useEffect(() => {
    setOpen(false);
    setExpandedMobile(null);
  }, [location.pathname]);

  // Anchor-links on home use smooth scroll; regular routes just navigate.
  const handleNav = (item, e) => {
    if (item.href && item.href.startsWith("/#")) {
      e.preventDefault();
      const anchor = item.id;
      if (location.pathname === "/") {
        const el = document.getElementById(anchor);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        navigate("/");
        setTimeout(() => {
          const el = document.getElementById(anchor);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 80);
      }
      setOpen(false);
    }
  };

  const textColor = overDark ? "text-[#F5F2EC]" : "text-[#1A1A1A]";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        overDark ? "bg-[#0E0E0E] border-b border-[#1F1F1F]" : "bg-[#F8F5EF] border-b border-[#EAE6DE]"
      }`}
    >
      <div className={`container-x flex items-center justify-between transition-all duration-500 ${scrolled ? "h-20" : "h-24 md:h-28"}`}>
        <Link to="/" data-testid="logo-home-link" className="flex items-center">
          <img
            src={overDark ? LOGO_LIGHT : LOGO_DARK}
            alt="Wertbild Immobilienfotografie"
            className={`w-auto block transition-all duration-500 ${scrolled ? "h-9 md:h-11" : "h-11 md:h-14"}`}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8 xl:gap-10">
          {NAV_ITEMS.map((item) =>
            item.children ? (
              <div key={item.label} className="relative group">
                <button
                  data-testid={`nav-${item.id}`}
                  className={`inline-flex items-center gap-1.5 text-[13px] tracking-wide ${textColor} hover:text-[#FFA500] transition-colors py-2`}
                >
                  {item.label}
                  <ChevronDown size={13} className="transition-transform duration-300 group-hover:rotate-180" />
                </button>
                {/* Dropdown */}
                <div
                  data-testid={`nav-${item.id}-dropdown`}
                  className="invisible opacity-0 group-hover:visible group-hover:opacity-100 absolute top-full left-1/2 -translate-x-1/2 mt-1 min-w-[280px] bg-[#F8F5EF] border border-[#EAE6DE] shadow-[0_18px_40px_-12px_rgba(0,0,0,0.15)] transition-all duration-300"
                >
                  {item.children.map((child) => (
                    <Link
                      key={child.id}
                      to={child.href}
                      data-testid={`nav-${child.id}`}
                      className="block px-6 py-4 text-[13px] text-[#1A1A1A] hover:bg-white hover:text-[#FFA500] transition-colors border-b border-[#EAE6DE] last:border-b-0"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <a
                key={item.label}
                href={item.href}
                data-testid={`nav-${item.id}`}
                onClick={(e) => handleNav(item, e)}
                className={`text-[13px] tracking-wide ${textColor} hover:text-[#FFA500] link-underline transition-colors`}
              >
                {item.label}
              </a>
            )
          )}
        </nav>

        <a
          href="/#kontakt"
          onClick={(e) => handleNav({ id: "kontakt", href: "/#kontakt" }, e)}
          data-testid="nav-cta-anfragen"
          className={`hidden lg:inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.18em] font-medium transition-colors duration-300 ${
            overDark
              ? "bg-[#FFA500] text-[#0E0E0E] hover:bg-[#F5F2EC]"
              : "bg-[#1A1A1A] text-[#F8F5EF] hover:bg-[#FFA500] hover:text-[#1A1A1A]"
          }`}
        >
          Projekt anfragen
          <ArrowUpRight size={14} />
        </a>

        <button
          aria-label="Menü"
          data-testid="mobile-menu-toggle"
          className={`lg:hidden p-2 ${textColor}`}
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile nav */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#F8F5EF] border-t border-[#EAE6DE] max-h-[calc(100vh-5rem)] overflow-y-auto">
          <div className="container-x py-6 flex flex-col gap-4">
            {NAV_ITEMS.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <button
                    onClick={() => setExpandedMobile(expandedMobile === item.id ? null : item.id)}
                    data-testid={`mobile-nav-${item.id}`}
                    className="w-full flex items-center justify-between text-[#1A1A1A] text-base hover:text-[#FFA500] transition-colors py-1"
                  >
                    <span>{item.label}</span>
                    <ChevronDown
                      size={16}
                      className={`transition-transform duration-300 ${expandedMobile === item.id ? "rotate-180" : ""}`}
                    />
                  </button>
                  {expandedMobile === item.id && (
                    <div className="mt-3 pl-4 border-l border-[#EAE6DE] flex flex-col gap-3">
                      {item.children.map((child) => (
                        <Link
                          key={child.id}
                          to={child.href}
                          data-testid={`mobile-nav-${child.id}`}
                          className="text-[#5A5A5A] text-sm hover:text-[#FFA500] transition-colors"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => handleNav(item, e)}
                  data-testid={`mobile-nav-${item.id}`}
                  className="text-[#1A1A1A] text-base hover:text-[#FFA500] transition-colors"
                >
                  {item.label}
                </a>
              )
            )}
            <a
              href="/#kontakt"
              onClick={(e) => handleNav({ id: "kontakt", href: "/#kontakt" }, e)}
              data-testid="mobile-cta-anfragen"
              className="mt-2 inline-flex items-center justify-center px-5 py-3 bg-[#1A1A1A] text-[#F8F5EF] text-sm uppercase tracking-[0.18em] font-medium"
            >
              Projekt anfragen
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
